/** ч.1398: «Обліковий запис → Баланс і тарифи» + консоль платформи «Баланс і тарифи клієнтів». */
import { api } from './client';

export interface Tariffs {
  period_days: number; trial_days: number; grace_days: number; purge_days: number;
  base_price: string; base_quota_mb: number; extra_block_mb: number; extra_block_price: string;
  edo_block_mb: number; edo_block_price: string; edo_free_docs: number;
  credits_pack: string; credits_pack_price: string; gift_credits: string; currency: string; vat: boolean;
}

export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'GRACE' | 'BLOCKED' | 'TESTER';

export interface BaseSubscription {
  organization_id: string;
  name: string;
  plan: 'BASE' | 'EDO';
  status: SubscriptionStatus;
  paid_until: string | null;
  grace_until: string | null;
  delete_after: string | null;
  extra_blocks: number;
  /** ч.1402: оплачені блоки (квота) і замовлені, що додадуться після оплати тарифу */
  paid_blocks: number;
  pending_blocks: number;
  quota_after_payment_bytes: number | null;
  block_mb: number;
  block_price: string;
  /** null — без обмеження за обсягом (картка ЕДО без оплачених блоків, ч.1408). */
  quota_bytes: number | null;
  used_bytes: number;
  files: number;
  monthly_price: string;
  restore_fee: string;
  days_left: number | null;
}

export interface BillingSummary {
  balance: string;
  /** ч.1399: власник платформи вказав організацію-продавця — рахунки виписуються */
  invoices_enabled: boolean;
  /** ч.1400: еквайринг підключено (токен у налаштуваннях платформи) */
  card_payments_enabled: boolean;
  tester: boolean;
  gift_month: string | null;
  credits_balance: string;
  is_owner: boolean;
  organizations: BaseSubscription[];
  monthly_total: string;
  tariffs: Tariffs;
}

export interface LedgerRow {
  id: string;
  kind: string;
  amount: string;
  balance_after: string;
  period_from: string | null;
  period_to: string | null;
  note: string | null;
  created_at: string;
  organization: string | null;
  /** ч.1427: дата надходження з виписки; акт за оплатою рахунка */
  receipt_date: string | null;
  can_form_act: boolean;
  act_id: string | null;
  act_status: ActStatus | null;
  act_number: string | null;
  act_payer_code: string | null;
  act_payer_name: string | null;
}

export type ActStatus = 'FORMED' | 'CLIENT_SIGNED' | 'DONE';

export interface BillingAct {
  id: string;
  number: string;
  act_date: string;
  amount: string;
  status: ActStatus;
  payer_name: string;
  payer_code: string | null;
}

export const ACT_STATUS_UK: Record<ActStatus, string> = {
  FORMED: 'очікує вашого підпису',
  CLIENT_SIGNED: 'на підписі в ТОВ «Ейзі Україна»',
  DONE: 'підписано обома сторонами',
};

export interface BillingNotice {
  organization_id: string; name: string; status: SubscriptionStatus; paid_until: string; days_left: number;
  grace_until: string | null; delete_after: string | null; price: string; balance: string; is_owner: boolean;
}

export const LEDGER_KIND_UK: Record<string, string> = {
  TOPUP_MANUAL: 'поповнення',
  TOPUP_INVOICE: 'поповнення за рахунком',
  TOPUP_CARD: 'поповнення карткою',
  CHARGE_BASE: 'тариф бази',
  CHARGE_STORAGE: 'додаткове сховище',
  CHARGE_RESTORE: 'відновлення доступу',
  CHARGE_CREDITS: 'кредити Oblik+/AI',
  ADJUST: 'коригування',
  REFUND: 'повернення',
};

export const STATUS_UK: Record<SubscriptionStatus, string> = {
  TRIAL: 'пробний період',
  ACTIVE: 'оплачено',
  GRACE: 'лише перегляд',
  BLOCKED: 'недоступна',
  TESTER: 'тестовий акаунт',
};

export type InvoiceStatus = 'ISSUED' | 'PAID' | 'CANCELLED';

export interface BillingInvoice {
  id: string;
  number: string;
  invoice_date: string;
  valid_until: string | null;
  amount: string;
  status: InvoiceStatus;
  payer_name: string;
  payer_code: string | null;
  payer_organization_id: string | null;
  email: string | null;
  paid_at: string | null;
  paid_amount: string | null;
  note: string | null;
  created_at: string;
  /** лише у відповіді на створення */
  email_sent?: boolean;
  /** лише у консолі платформи */
  tenant_name?: string;
  tenant_id?: string;
}

export type CardPaymentStatus = 'created' | 'processing' | 'hold' | 'success' | 'failure' | 'reversed' | 'expired';

export interface CardPayment {
  id: string;
  provider: string;
  provider_invoice_id: string;
  amount: string;
  status: CardPaymentStatus;
  page_url: string | null;
  credited: boolean;
  paid_amount: string | null;
  fee: string | null;
  failure_reason: string | null;
  created_at: string;
  paid_at: string | null;
}

export const CARD_STATUS_UK: Record<CardPaymentStatus, string> = {
  created: 'очікує оплати',
  processing: 'обробляється',
  hold: 'заблоковано на картці',
  success: 'оплачено',
  failure: 'не оплачено',
  reversed: 'повернено',
  expired: 'прострочено',
};

export const INVOICE_STATUS_UK: Record<InvoiceStatus, string> = {
  ISSUED: 'очікує оплати',
  PAID: 'оплачено',
  CANCELLED: 'скасовано',
};

export interface EdoParticipantIn {
  entity_type: 'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL';
  name: string;
  code: string;
  email?: string | null;
}

export const edoParticipants = {
  create: (p: EdoParticipantIn) =>
    api.post<{ id: string; name: string; entity_type: string; code: string }>('/edo/participants', p).then((r) => r.data),
  update: (id: string, p: EdoParticipantIn) =>
    api.patch<{ id: string; name: string; entity_type: string; code: string }>(`/edo/participants/${id}`, p).then((r) => r.data),
};

export const billing = {
  summary: () => api.get<BillingSummary>('/billing/summary').then((r) => r.data),
  ledger: (limit = 200) => api.get<{ items: LedgerRow[]; total: number }>('/billing/ledger', { params: { limit } }).then((r) => r.data),
  notices: () => api.get<BillingNotice[]>('/billing/notices').then((r) => r.data),
  renew: (orgId: string) => api.post<{ paid_until: string; charged: string; gifted: boolean }>(`/billing/organizations/${orgId}/renew`).then((r) => r.data),
  restore: (orgId: string) => api.post<{ paid_until: string; charged: string; gifted: boolean }>(`/billing/organizations/${orgId}/restore`).then((r) => r.data),
  setStorage: (orgId: string, blocks: number) => api.post<{ extra_blocks: number; paid_blocks: number; charged: string; monthly_price?: string }>(`/billing/organizations/${orgId}/storage`, { blocks }).then((r) => r.data),
  buyCredits: (packs: number) => api.post<{ credits: string; charged: string; credits_balance: string }>('/billing/credits/buy', { packs }).then((r) => r.data),
  invoices: () => api.get<{ items: BillingInvoice[]; enabled: boolean }>('/billing/invoices').then((r) => r.data),
  requestInvoice: (p: { amount: number; organization_id?: string | null; payer_name?: string | null; payer_code?: string | null; email?: string | null }) =>
    api.post<BillingInvoice>('/billing/invoices', p).then((r) => r.data),
  cancelInvoice: (id: string) => api.post<BillingInvoice>(`/billing/invoices/${id}/cancel`).then((r) => r.data),
  checkPayment: (id: string) => api.post<{ status: InvoiceStatus; queued: boolean }>(`/billing/invoices/${id}/check-payment`).then((r) => r.data),
  formAct: (ledgerId: string) => api.post<BillingAct>('/billing/acts', { ledger_id: ledgerId }).then((r) => r.data),
  signAct: (id: string, signatureB64: string) => api.post<BillingAct>(`/billing/acts/${id}/sign`, { signature_b64: signatureB64 }, { headers: { 'X-Silent-422': '1' } }).then((r) => r.data),
  actBlob: (id: string) => api.get(`/billing/acts/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),
  downloadActPdf: async (id: string, filename: string): Promise<void> => {
    const r = await api.get(`/billing/acts/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
  legalNotice: () => api.get<{ text: string; until: string } | null>('/billing/legal-notice').then((r) => r.data),
  mode: () => api.get<{ has_orgs: boolean; all_edo: boolean; edo_org_ids: string[] }>('/billing/mode').then((r) => r.data),
  setPlan: (orgId: string, plan: 'BASE') =>
    api.post<{ plan: string; charged: string; paid_until?: string; gifted?: boolean }>(`/billing/organizations/${orgId}/plan`, { plan }).then((r) => r.data),
  cardPayments: () => api.get<{ items: CardPayment[]; enabled: boolean }>('/billing/card-payments').then((r) => r.data),
  createCardPayment: (amount: number) => api.post<CardPayment>('/billing/card-payments', { amount }).then((r) => r.data),
  cardPayment: (id: string) => api.get<CardPayment>(`/billing/card-payments/${id}`).then((r) => r.data),
  downloadInvoicePdf: async (id: string, filename: string): Promise<void> => {
    const r = await api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
};

export interface PlatformBillingRow {
  tenant_id: string; name: string; is_tester: boolean; status: string; balance: string; gift_month: string | null;
  bases: number; active: number; trial: number; unpaid: number; next_due: string | null; owners: string | null;
}

export const platformBilling = {
  list: () => api.get<{ tariffs: Tariffs; items: PlatformBillingRow[] }>('/platform/billing').then((r) => r.data),
  topup: (p: { tenant_id: string; amount: number; kind: string; note?: string | null }) =>
    api.post<{ tenant_id: string; balance: string }>('/platform/billing/topup', p).then((r) => r.data),
  adjust: (p: { tenant_id: string; amount: number; note: string }) =>
    api.post<{ tenant_id: string; balance: string }>('/platform/billing/adjust', p).then((r) => r.data),
  detail: (tenantId: string) => api.get<{ balance: string; ledger: LedgerRow[];
    subscriptions: Array<{ organization_id: string; name: string; plan: string; status: string; paid_until: string | null;
      extra_blocks: number; grace_until: string | null; delete_after: string | null; used_bytes: number; monthly_price: string }> }>(
    `/platform/billing/tenants/${tenantId}`).then((r) => r.data),
  tick: () => api.post<Record<string, number>>('/platform/billing/tick').then((r) => r.data),
  settings: () => api.get<PlatformBillingSettings>('/platform/billing/settings').then((r) => r.data),
  saveSettings: (p: { vendor_org_id: string | null; invoice_valid_days: number; invoice_note: string | null; acquiring_pubkey?: string | null }) =>
    api.put<PlatformBillingSettings>('/platform/billing/settings', p).then((r) => r.data),
  invoices: (status?: string) => api.get<{ items: BillingInvoice[] }>('/platform/billing/invoices', { params: status ? { status_filter: status } : {} }).then((r) => r.data),
  markPaid: (id: string, note: string) => api.post<BillingInvoice>(`/platform/billing/invoices/${id}/mark-paid`, { note }).then((r) => r.data),
  cancelInvoice: (id: string) => api.post<{ id: string; status: string }>(`/platform/billing/invoices/${id}/cancel`).then((r) => r.data),
  match: () => api.post<{ matched: number; checked: number; review: number }>('/platform/billing/match').then((r) => r.data),
  incoming: (status: string) => api.get<IncomingList>('/platform/billing/incoming', { params: { status_filter: status } }).then((r) => r.data),
  creditIncoming: (id: string, p: { invoice_id?: string | null; tenant_id?: string | null; note: string }) =>
    api.post<{ id: string; status: string }>(`/platform/billing/incoming/${id}/credit`, p).then((r) => r.data),
  ignoreIncoming: (id: string, note: string) => api.post<{ id: string; status: string }>(`/platform/billing/incoming/${id}/ignore`, { note }).then((r) => r.data),
  reopenIncoming: (id: string) => api.post<{ id: string; status: string }>(`/platform/billing/incoming/${id}/reopen`).then((r) => r.data),
};

export type IncomingStatus = 'REVIEW' | 'CREDITED' | 'IGNORED';

export interface IncomingPayment {
  id: string;
  bank_receipt_id: string;
  receipt_date: string;
  amount: string;
  payer_name: string | null;
  payer_code: string | null;
  purpose: string | null;
  status: IncomingStatus;
  how: string | null;
  reason: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  suggested_invoice_id: string | null;
  suggested_number: string | null;
  suggested_amount: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  post_error: string | null;
  note: string | null;
}

export interface IncomingList {
  items: IncomingPayment[];
  counts: Partial<Record<IncomingStatus, number>>;
  acts_to_sign: number;
  acts_not_posted: number;
}

export const INCOMING_HOW_UK: Record<string, string> = {
  NUMBER_AND_CODE: 'номер рахунка і код платника',
  NUMBER_AND_SUM: 'номер рахунка і сума',
  CODE_AND_SUM: 'код платника і сума',
  MANUAL: 'вручну',
};

export interface PlatformBillingSettings {
  vendor_org_id: string | null;
  vendor_owner_id: string | null;
  invoice_valid_days: number;
  invoice_note: string | null;
  /** ч.1400: відкритий ключ mono (base64 PEM) для перевірки вебхуків; порожній — береться з API */
  acquiring_pubkey: string | null;
  acquiring_enabled: boolean;
  organizations: Array<{ id: string; name: string; code: string | null; tenant_name: string; has_iban: boolean }>;
}

export const fmtUah = (v: string | number) =>
  `${Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;

export const fmtDate = (iso: string | null | undefined) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—';

export const fmtBytes = (n: number): string => {
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 ** 3)).toFixed(2)} ГБ`;
  if (n >= 1024 * 1024) return `${(n / (1024 ** 2)).toFixed(1)} МБ`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${n} Б`;
};
